// store/Auth/authThunks.js
import { createAsyncThunk } from "@reduxjs/toolkit";
import apiClient from "../../api/client";
import { loginRequest } from "../../config/authConfig";
import ENDPOINTS from "../../api/endpoints";
import { showNotification } from "../Notifications/notificationsSlice";

export const verifyUser = createAsyncThunk(
  "auth/verifyUser",
  async ({ instance, account, token } = {}, { rejectWithValue }) => {
    try {
      let authToken = token;

      // Only try to acquire token if instance & account are provided (Azure)
      if (!authToken && instance && account) {
        const response = await instance.acquireTokenSilent({
          ...loginRequest,
          account,
        });
        authToken = response.idToken || response.accessToken;
      }

      const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};

      const response = await apiClient.get(ENDPOINTS.AUTH.VERIFY, {
        headers,
        withCredentials: true, // ensure cookies are sent for Basic Auth
      });

      return {
        ...response.data,
        loginMethod: authToken ? "msal" : "basic",
      };
    } catch (error) {
      console.error("❌ verifyUser failed:", {
        message: error.message,
        response: error.response,
        data: error.response?.data,
        status: error.response?.status,
      });

      return rejectWithValue(
        error.response?.data?.message ||
          error.response?.data?.error || // if your Flask app uses "error"
          error.message
      );
    }
  }
);

export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async ({ email, password }, { rejectWithValue }) => {
    try {
      // For basic auth, the tokens are set in cookies.
      const response = await apiClient.post(ENDPOINTS.AUTH.LOGIN, {
        username: email,
        password,
      });
      console.log("Login response:", response.data);
      return { ...response.data, loginMethod: "basic" };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Login failed");
    }
  }
);

export const logoutUser = createAsyncThunk(
  "auth/logoutUser",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(ENDPOINTS.AUTH.LOGOUT, {});
      return { ...response.data, loginMethod: null };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Logout failed");
    }
  }
);

export const forgotPassword = createAsyncThunk(
  "auth/forgotPassword",
  async ({ email }, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(ENDPOINTS.AUTH.FORGOT_PASSWORD, {
        email,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || "Forgot password request failed"
      );
    }
  }
);

export const resetPassword = createAsyncThunk(
  "auth/resetPassword",
  async ({ token, newPassword }, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(ENDPOINTS.AUTH.RESET_PASSWORD, {
        token,
        new_password: newPassword,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || "Reset password failed"
      );
    }
  }
);

export const markTrainingVideoComplete = createAsyncThunk(
  "users/markTrainingVideoComplete",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const response = await apiClient.patch(
        ENDPOINTS.USERS.MARK_TRAINING_VIDEO_COMPLETED
      );

      dispatch(
        showNotification({
          message: "Training video marked as seen",
          type: "success",
        })
      );

      return response.data.data; // updated user
    } catch (error) {
      dispatch(
        showNotification({
          message: "Failed to mark training video",
          type: "error",
        })
      );

      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchTrainingVideos = createAsyncThunk(
  "users/fetchTrainingVideos",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const response = await apiClient.get(
        ENDPOINTS.USERS.FETCH_TRAINING_VIDEOS
      );

      console.log("Training videos response:", response.data);

      return response.data.data; // [{ id, filename, title, url }]
    } catch (error) {
      dispatch(
        showNotification({
          message: "Failed to load training videos",
          type: "error",
        })
      );
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);
